<template>
	<v-card class="comment-card elevation-0">
		<div class="d-flex flex-fill comment-card__outer-box">
			<div class="comment-card__info-box" style="width: 100%">
				<div
					class="comment-card__link d-flex justify-space-between align-start text-decoration-none px-3 pt-3"
					:class="{ disabled: this.post.user.user_hidden }"
				>
					<div
						class="d-flex align-start cursor-pointer"
						@click="toggleToUser"
						style="gap: 12px"
					>
						<Avatar
							class="comment-card__avatar"
							:size="40"
							:avatar="comment.authorAvatar"
						/>
						<div class="d-flex flex-column justify-space-between">
							<span class="comment-card__title">
								{{ comment.authorName }}
							</span>
							<span class="comment-card__subtitle">
								{{ comment.user.second_profile_row }}
							</span>
						</div>
					</div>
					<div
						class="comment-card__date"
						:class="{ 'no-ellipsis': !isOwnCurrentUserComment }"
					>
						<button
							@click="openDeleteModal"
							class="comment-card__date__button"
							:class="{
								'forum-card__meta-time__button--active': isModal,
							}"
							type="button"
							v-if="isOwnCurrentUserComment"
						>
							<Icon type="ellipsis" :size="24" color="var(--accent-color)" />
						</button>
						<div
							v-click-outside="closeModal"
							v-if="isModal"
							class="comment-card__date__button__reply-modal"
						>
							<Icon type="delete" :size="24" color="#FF3B30" />
							<button @click="handleDeleteComment(comment)" type="button">
								Delete
							</button>
						</div>
						{{ comment.date }}
					</div>
				</div>

				<v-card-actions v-if="isAuthenticated" class="px-0 py-0 comment-card__input-area">
					<CommentEditForm
						v-if="isEdit"
						:comment="comment"
						v-click-outside="hideEditInput"
						@success="
							() => {
								this.isEdit = false
							}
						"
					/>
					<v-card-text
						v-else-if="contentFormateChangeMentions.length"
						class="px-3 py-3 comment-card__content"
					>
						<span v-for="(item, index) in contentFormateChangeMentions" :key="index">
							<router-link v-if="item.link" :to="item.link">
								{{ item.value }}
							</router-link>
							<span v-else>
								{{ item.value }}
							</span>
						</span>
					</v-card-text>
					<v-card-text v-else class="px-3 pt-4 pb-4 comment-card__content pl-3">
						<span>{{ this.comment.content }}</span>
					</v-card-text>
				</v-card-actions>

				<v-card-actions
					v-if="isAuthenticated"
					class="comment-card__actions d-flex align-center justify-space-between px-3 py-3"
				>
					<div class="d-flex align-center">
						<span
							:class="
								isCurrentUserLike
									? 'comment-card__like-btn'
									: 'comment-card__like-btn-active'
							"
							@click="handleLikeComment(comment)"
						>
							<Icon
								type="hearth"
								:color="isCurrentUserLike ? 'var(--text_button_tint)' : '#a0a0a0'"
								:size="24"
								:pr="8"
							/>
							<span>
								{{ upvoteCount }}
							</span>
						</span>
						<span class="comment-card__replies-count mr-4 ml-4">
							<Icon type="comment" color="#9b9b9b" :size="24" :pr="8" />
							<span class="comment-card__replies-count-num">
								{{ repliesCount }}
							</span>
						</span>
					</div>

					<div class="comment-card__buttons">
						<button
							v-if="!isReply"
							class="comment-card__reply-btn mt-0 text--black"
							@click="replyHandler"
							v-ripple="false"
							type="button"
						>
							Reply
						</button>
					</div>
				</v-card-actions>

				<ReplyCommentForm
					v-if="isReply"
					:comment="comment"
					:autoMentionUser="comment.user"
					:post="post"
					v-click-outside="hideInput"
					:isReply="isReply"
					@success="successReply"
					:class="{ 'comment-card__reply-last': replies.length === 0 }"
				/>

				<Reply
					v-for="reply in replies"
					:key="reply.id"
					:comment="reply"
					:post="{
						thread_id: reply.threadId,
						user: reply.user,
					}"
					:parent-comment="comment"
				/>
			</div>
		</div>
	</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { normalizeComment } from '@/utils/helpers'

import Avatar from '@/components/Avatar'
import ReplyCommentForm from '@/components/Forms/ReplyCommentForm'
import CommentEditForm from '@/components/Forms/CommentEditForm'
import Reply from '@/components/Reply'
import Dwh from '@/mixins/Dwh'

export default {
	components: {
		Avatar,
		ReplyCommentForm,
		CommentEditForm,
		Reply,
	},
	mixins: [Dwh],
	props: {
		comment: {
			type: Object,
			default: () => ({}),
		},
		post: {
			type: Object,
			default: () => ({}),
		},
	},
	data: () => ({
		isReply: false,
		inputFocus: false,
		isEdit: false,
		isLike: false,
		isModal: false,
	}),
	computed: {
		...mapGetters('auth', ['profile', 'isAuthenticated']),
		userName() {
			return this.profile.name
		},
		/**
		 * @returns Array
		 */
		replies() {
			return this.comment.replies.map((item) => normalizeComment(item)).reverse()
		},
		isCurrentUserLike() {
			return !!this.comment.upvote
		},
		upvoteCount() {
			return this.comment.upvoteCount || 0
		},
		repliesCount() {
			return this.comment.replies.length
		},
		isOwnCurrentUserComment() {
			return this.comment.userId === this.profile.user_id
		},
		dateCreated() {
			const date = new Date(this.comment.date)
			const options = { year: 'numeric', month: 'long', day: 'numeric' }
			return date.toLocaleDateString('en-US', options)
		},
		contentFormateChangeMentions() {
			const content = this.comment.content
			const pattern = /@\[\d+\|[^]+?\]/g
			const matches = content.match(pattern)
			let result = content
			let contentArray = []

			if (matches) {
				for (let i = 0; i < matches.length; i++) {
					const user_name = matches[i].match(/\|(.+?)\]/)[1]
					const user_id = matches[i].match(/\[(.*?)\|/)[1]

					const replacement = `@${user_name}`
					result = result.replace(matches[i], replacement)
					const startIndex = result.indexOf(`@${user_name}`)
					const endIndex = startIndex + user_name.length + 1
					if (startIndex > 0) {
						contentArray.push({ value: result.substring(0, startIndex) })
					}
					contentArray.push({
						value: result.substring(startIndex, endIndex),
						link: { name: 'user-by-id', params: { id: user_id } },
					})
					if (i + 1 === matches.length && result.substring(endIndex)) {
						contentArray.push({ value: result.substring(endIndex) })
					} else {
						result = result.substring(endIndex)
					}
				}
			}
			return contentArray
		},
	},
	methods: {
		...mapActions('post', ['deleteComment', 'likeComment']),
		...mapActions('forum', ['setActiveComment']),
		closeModal() {
			this.isModal = false
		},
		openDeleteModal() {
			this.isModal = !this.isModal
		},
		toggleToUser() {
			const isOwn = this.isCurrentUser(this.comment.user.user_id)
			return this.$router.push({
				name: isOwn ? 'profile' : 'user-by-id',
				params: isOwn ? {} : { id: this.comment.user.user_id },
			})
		},

		successReply() {
			this.isReply = false
			this.setActiveComment(this.isReply)
		},

		isCurrentUser(userId) {
			if (!this.profile) {
				return false
			}

			const { user_id: currentUserId } = this.profile
			return userId === currentUserId
		},

		handleLikeComment(comment) {
			this.likeComment(comment)
		},
		handleDeleteComment(comment) {
			this.deleteComment(comment)
			this.isModal = false
		},
		replyHandler() {
			this.inputFocus = true
			if (!this.isReply) {
				this.setActiveComment(true)
			}
			this.isReply = true
			if (this.isReply) {
				this.sendTrigger({
					category: 'Content',
					action: 'click_reply_forum_post_details',
					module: 'Forums',
					chapter_id: this.$route.params.id,
				})
			}
		},

		hideInput() {
			if (this.isReply) {
				this.setActiveComment(false)
			}
			this.isReply = false
		},
		hideEditInput() {
			this.isEdit = false
		},
	},
}
</script>

<style lang="scss">
.v-card {
	&__actions {
		align-items: flex-start;
	}
}

.comment-card {
	font-family: Inter, sans-serif;
	font-style: normal;
	font-weight: normal;
	background-color: #f3f3f6 !important;
	&__reply-last {
		border-bottom-left-radius: 8px;
		border-bottom-right-radius: 8px;
		background-color: #ffffff;
		border-bottom: 1.5px solid #e2e2e2 !important;
	}

	/*&__link {
		height: 64px;
	}*/

	&__buttons {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	&__actions {
		border-bottom: 1px solid #e2e2e2;
		border-top: 1px solid #e2e2e2;
		height: 48px;
	}

	&__title {
		font-family: Inter, sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		letter-spacing: 0;
		color: var(--username-category-title);
		white-space: nowrap;
	}

	&__avatar {
		border-radius: 8px;
	}

	&__subtitle {
		font-family: Inter, sans-serif;
		font-weight: 400;
		font-size: 15px;
		line-height: 20px;
		letter-spacing: 0;
    word-break: break-word;
		color: var(--company-position-large);
	}

	&__date {
		max-width: 364px;
		height: 46px;
		width: 100%;
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		justify-content: space-between;
		font-size: 13px;
		color: #697583;
		font-family: Inter, sans-serif;
		font-weight: 400;
		line-height: 100%;
		letter-spacing: 0;
		text-align: right;
		user-select: none;
		&.no-ellipsis {
			justify-content: flex-end;
		}
		&__button {
			width: 24px;
			height: 24px;
			position: relative;
			&__reply-modal {
				position: absolute;
				width: 250px;
				height: 72px;
				border-radius: 8px;
				background: #ffffff;
				border: 1px solid #e2e2e2;
				top: 44px;
				display: flex;
				align-items: center;
				gap: 8px;
				font-family: 'Inter', sans-serif;
				font-weight: 400;
				font-size: 14px;
				line-height: 100%;
				letter-spacing: 0;
				color: #ff3b30;
				padding: 24px;
			}
		}
	}

	&__reply-btn {
		font-family: Inter, sans-serif;
		font-weight: 700;
		font-size: 14px;
		line-height: 100%;
		letter-spacing: 0;
		text-align: center;
		color: var(--text_button_tint);
	}

	&__reply {
		border-left: 1px solid rgba(0, 0, 0, 0.2);
		padding-left: 10px;
	}

	&__reply-info {
		display: flex;
		flex-direction: column;
		margin-bottom: 10px;
	}

	&__reply-author {
		font: 14px/1.2 'Inter', sans-serif !important;
		color: #525252 !important;
	}

	&__reply-date {
		font: 12px/1.2 'Inter', sans-serif !important;
		padding-top: 5px;
		color: rgba(0, 0, 0, 0.4) !important;
	}

	&__send-btn {
		width: 110px;
		height: 30px !important;
		border-radius: 24.5px;
		background-color: var(--accent-color) !important;
		color: #ffffff !important;
		font-family: Inter, sans-serif;
		text-transform: none;
		font-style: normal;
		font-weight: 500;
		font-size: 14px;
		line-height: 16px;
		text-align: center;

		&:hover {
			color: #fff !important;
			background-color: var(--accent-color) !important;
			opacity: 1;
		}
	}

	&__input-area {
		a {
			text-decoration: none;
		}
	}

	&__reply-field {
		border: 1px solid rgba(0, 0, 0, 0.15) !important;
		padding: 10px;
		margin-bottom: 10px;

		&:focus {
			outline: none !important;
		}
	}

	&__content {
		font-size: 16px;
		line-height: 25px;
		color: #000000;

		& a {
			font-weight: bold;
			color: #42a0d8;
			cursor: pointer;
		}
	}

	&__like-btn,
	&__like-btn-active {
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 13px;
		height: 16px;
		line-height: 16px;
		cursor: pointer;
		display: flex;
		letter-spacing: 0;
		align-items: center;
		user-select: none;
	}

	&__like-btn {
		i,
		span {
			color: var(--text_button_tint) !important;
		}

		&-active {
			i,
			span {
				color: #a0a0a0 !important;
			}
		}
	}

	&__replies-count {
		display: flex;
		align-items: center;

		&-icon {
			color: var(--accent-color) !important;
			position: relative;
			top: 2px;
		}

		&-num {
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 13px;
			line-height: 100%;
			letter-spacing: 0;
			color: #9b9b9b;
		}
	}

	@media screen and (max-width: 400px) {
		&__outer-box {
			flex-direction: column;
		}
		&__info-box {
			width: 100%;
		}
	}
}
</style>
